import { useEffect } from "react";

const ParticipantsList = ({
  participants,
  isOpen,
  onClose,
  isHost = false,
  myName = "",
  voiceEnabled = false,
  mutedParticipants = [],
  onToggleMute,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
        data-testid="participants-backdrop"
      />

      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 transform transition-transform">
        <div className="bg-black/90 backdrop-blur-xl border-t border-white/20 rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
          {/* Handle */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-1.5 bg-white/30 rounded-full"></div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 
              className="text-2xl font-primary text-[#00F0FF]"
              style={{ fontFamily: 'Anton, sans-serif' }}
            >
              PARTICIPANTS ({participants.length})
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-[#FF0055] transition-colors"
              data-testid="close-participants-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Participants List */}
          <div className="space-y-3" data-testid="participants-list">
            {participants.length === 0 ? (
              <p className="text-center text-[#A1A1AA] py-8">No participants yet</p>
            ) : (
              participants.map((participant, index) => {
                const isConnected = Boolean(participant.socket_id);
                const isMuted = mutedParticipants.includes(participant.name);
                const isMe = participant.name === myName;
                const canToggle = voiceEnabled && isConnected && onToggleMute && (isHost || isMe);
                return (
                  <div
                    key={index}
                    className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
                    data-testid={`participant-item-${index}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF0055] to-[#00F0FF] flex items-center justify-center font-primary text-lg">
                      {participant.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">
                        {participant.name}
                        {isMe && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#A1A1AA]">(you)</span>}
                      </p>
                      {participant.is_host && (
                        <p className="text-xs text-[#FFEA00]">Host</p>
                      )}
                    </div>
                    {canToggle && (
                      <button
                        type="button"
                        onClick={() => onToggleMute(participant.name, !isMuted)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                          isMuted
                            ? "bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10"
                            : "bg-[#9D4EDD]/20 border border-[#9D4EDD]/50 text-[#9D4EDD] hover:bg-[#9D4EDD]/30"
                        }`}
                        title={isMuted ? `Unmute ${isMe ? "yourself" : participant.name}` : `Mute ${isMe ? "yourself" : participant.name}`}
                        data-testid={`toggle-mute-${participant.name}`}
                      >
                        {isMuted ? "🔇" : "🎙️"}
                      </button>
                    )}
                    <div className="w-2 h-2 bg-[#00F0FF] rounded-full animate-pulse"></div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ParticipantsList;
